/**
 * The chat's money formatter — one implementation, because there were three.
 *
 * Every figure the chat renders is already denominated in some currency the
 * SERVER chose: the deployment's billing currency for the 'user' view, USD for
 * the 'operator' view (0174). The amount cannot say which, so the caller must
 * pass it, and there is deliberately NO default — a default is how a hardcoded
 * `$` came back twice, most recently in front of a Malaysian tenant's ringgit.
 *
 * Lives in its own module rather than in `ChatPanel` so any other chat surface
 * can import it without a cycle — the since-removed `RevertConflictModal` (#768)
 * is exactly where a second, unfixed copy of this was once found.
 */

/**
 * Format an amount in major units of `currencyCode`.
 *
 * Sub-cent precision is preserved: an individual turn is routinely worth less
 * than one minor unit, and rounding it to 0.00 makes the per-message badge
 * useless exactly where a user is checking whether a model is expensive.
 */
export function formatChatCost(amountMajor: number, currencyCode: string): string {
  const digits = amountMajor === 0 ? 2 : amountMajor < 0.01 ? 4 : amountMajor < 1 ? 3 : 2
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amountMajor)
  } catch {
    // An unsupported code must not blank the figure. Never silently relabel it
    // as USD — that is the defect this function exists to remove.
    return `${currencyCode.toUpperCase()} ${amountMajor.toFixed(digits)}`
  }
}
